/**
 * artifact-parser.js — extracts Canvas-worthy artifacts from an assistant
 * reply so the chat UI can offer "Open in Canvas" for rich content.
 *
 * An artifact is anything worth previewing in a side pane rather than reading
 * inline:
 *   • HTML page or fragment (`html`)
 *   • Standalone SVG (`svg`)
 *   • Markdown document (`markdown` / `md`)
 *   • Code snippet in any language (`code`)
 *
 * The scan is deliberately conservative:
 *   • Only fenced code blocks (```lang ...```) are considered — inline back-
 *     ticks stay inline.
 *   • Blocks < 40 chars OR < 4 lines are treated as inline noise, not
 *     artifacts, so a one-line command doesn't clutter the chat with a chip.
 *   • Every artifact keeps its exact fenced-block position (start/end index
 *     in the source) so the chat renderer can strip it and replace with a
 *     compact "Open in Canvas" chip.
 */

const MIN_ARTIFACT_CHARS = 40;
const MIN_ARTIFACT_LINES = 4;

const HTML_LANGS      = new Set(["html", "htm"]);
const SVG_LANGS       = new Set(["svg"]);
const MARKDOWN_LANGS  = new Set(["md", "markdown"]);
// Everything else is treated as generic "code" — we don't try to render it,
// just show a syntax-highlighted preview.

/**
 * Return an array of artifacts detected in `text`. Each artifact:
 *   { id, kind, lang, code, title, start, end }
 * where `kind` ∈ "html" | "svg" | "markdown" | "code".
 */
export function parseArtifacts(text) {
  if (!text || typeof text !== "string") return [];
  const out = [];
  const fence = /```([a-zA-Z0-9_+.\-]*)\n([\s\S]*?)```/g;
  let match;
  let idx = 0;
  while ((match = fence.exec(text)) !== null) {
    const lang = (match[1] || "").trim().toLowerCase();
    const code = match[2] || "";
    if (!isSubstantial(code)) continue;

    let kind = "code";
    if (HTML_LANGS.has(lang) || looksLikeHtml(code)) kind = "html";
    else if (SVG_LANGS.has(lang) || looksLikeSvg(code)) kind = "svg";
    else if (MARKDOWN_LANGS.has(lang)) kind = "markdown";

    out.push({
      id: `artifact-${idx++}`,
      kind,
      lang: lang || guessLang(code, kind),
      code,
      title: humanTitle(kind, lang),
      start: match.index,
      end: match.index + match[0].length,
    });
  }
  return out;
}

function isSubstantial(code) {
  const trimmed = (code || "").trim();
  if (trimmed.length < MIN_ARTIFACT_CHARS) return false;
  if (trimmed.split("\n").length < MIN_ARTIFACT_LINES) return false;
  return true;
}

function looksLikeHtml(code) {
  const t = code.trim().slice(0, 400).toLowerCase();
  if (t.startsWith("<!doctype") || t.startsWith("<html")) return true;
  // Fragment with a top-level element is enough
  if (/^<[a-z][a-z0-9]*[\s/>]/i.test(t) && /<\/[a-z][a-z0-9]*>/i.test(code)) return true;
  return false;
}

function looksLikeSvg(code) {
  return /<svg[\s>]/i.test(code) && /<\/svg>/i.test(code);
}

function guessLang(code, kind) {
  if (kind === "html") return "html";
  if (kind === "svg") return "svg";
  if (kind === "markdown") return "md";
  if (/^\s*(import |from |def |class |print\()/m.test(code)) return "python";
  if (/^\s*(const |let |var |function |import .*from)/m.test(code)) return "javascript";
  return "text";
}

function humanTitle(kind, lang) {
  if (kind === "html") return "HTML preview";
  if (kind === "svg") return "SVG";
  if (kind === "markdown") return "Document";
  const nice = (lang || "").replace(/^./, c => c.toUpperCase());
  return nice ? `${nice} snippet` : "Snippet";
}
